import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowLeft, Layers, Filter, Radar, Settings2, EyeOff, AlertTriangle, Sparkles, Target, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";

/**
 * Screener Blueprint — one-page introspection of the scanner. Admin-only.
 * Answers "what's in our universe, what do we screen on, what are we
 * missing?" without opening the codebase.
 */
export default function AdminScreenerBlueprint() {
  const [data, setData] = useState(null);
  const [attr, setAttr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/admin/screener-blueprint"),
      api.get("/admin/conviction-attribution").catch(() => ({ data: null })),
    ]).then(([b, a]) => {
      setData(b.data);
      setAttr(a.data);
    }).finally(() => setLoading(false));
  }, []);

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-mb-sand flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-mb-emerald" />
      </div>
    );
  }

  return (
    <div className="bg-mb-sand min-h-screen">
      <div className="max-w-5xl mx-auto px-6 pt-10 pb-20">
        {/* Breadcrumb */}
        <Link
          to="/admin"
          className="inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-mb-ink mb-6"
          data-testid="blueprint-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Vetting cockpit
        </Link>

        {/* Header */}
        <div className="uppercase tracking-widest text-[11px] text-mb-violet font-bold">
          Analyst desk · Scanner introspection
        </div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2 text-mb-ink">
          Screener Blueprint
        </h1>
        <p className="mt-3 text-neutral-600 max-w-2xl leading-relaxed">
          What's in our universe, what we screen on, where the gaps are.
          Answer "why did we miss X?" here before opening the codebase.
        </p>

        {/* Publish gates — every live rule a candidate must pass */}
        {data.publish_gates && (
          <Card icon={ShieldCheck} title="Publish gates (live)" testId="blueprint-publish-gates" accent="emerald">
            <ul className="divide-y divide-mb-border">
              {data.publish_gates.map((g) => (
                <li key={g.name} className="py-2 flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 text-sm" data-testid={`publish-gate-${g.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <span className="sm:w-44 flex-shrink-0 font-semibold text-mb-ink">{g.name}</span>
                  <span className="flex-1 text-neutral-700 font-mono-data text-xs sm:text-sm">{g.rule}</span>
                  <span className="text-[11px] uppercase tracking-wide text-neutral-500">{g.applies}</span>
                </li>
              ))}
            </ul>
            <div className="mt-3 text-[11px] italic text-neutral-500">Read live from the scanner settings — changes in Scanner filters show here immediately.</div>
          </Card>
        )}

        {/* Universe */}
        <Card icon={Layers} title="Universe" testId="blueprint-universe">
          <div className="flex flex-wrap gap-4">
            <Stat label="Total symbols" value={data.universe.total} />
            {Object.entries(data.universe.by_tier).map(([tier, n]) => (
              <Stat key={tier} label={tier.toUpperCase()} value={n} />
            ))}
            <Stat label="Runtime added" value={data.universe.runtime_additions} muted />
            {data.universe.smallcap_expansion > 0 && <Stat label="Small-cap expansion" value={data.universe.smallcap_expansion} />}
            <Stat label="Spike watchlist" value={data.universe.spike_watchlist} muted />
          </div>
          <div className="mt-4 text-xs text-neutral-600">
            Default scan tiers: <b className="text-mb-ink">{data.universe.default_scan_tiers.join(" · ")}</b>{" "}
            (mega excluded — they barely move %-wise).{" "}
            Breakout sweet spot: <b className="text-mb-ink">{data.universe.breakout_tiers.join(" · ")}</b>.
          </div>
        </Card>

        {/* Stage 1 — Pressure Index */}
        <Card icon={Filter} title={`Stage 1 · Pressure Index (${data.stage1_pressure_index.min_components_fired}/6 required)`} testId="blueprint-stage1">
          <ul className="space-y-2">
            {data.stage1_pressure_index.components.map((c) => (
              <li key={c.name} className="flex items-start gap-3 text-sm">
                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-mb-emerald flex-shrink-0" />
                <div>
                  <div className="font-semibold text-mb-ink">{c.name}</div>
                  <div className="text-xs text-neutral-600 font-mono-data">{c.rule}</div>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-3 text-[11px] italic text-neutral-500">{data.stage1_pressure_index.note}</div>
        </Card>

        {/* Stage 2 — Conviction */}
        <Card icon={Sparkles} title="Stage 2 · Conviction Stack" testId="blueprint-stage2">
          <ul className="space-y-2">
            {data.stage2_conviction_stack.signals.map((s) => (
              <li key={s.name} className="flex items-start gap-3 text-sm">
                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-mb-violet flex-shrink-0" />
                <div>
                  <div className="font-semibold text-mb-ink">{s.name}</div>
                  <div className="text-xs text-neutral-600 font-mono-data">{s.rule}</div>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-3 text-[11px] italic text-neutral-500">{data.stage2_conviction_stack.note}</div>
        </Card>

        {/* Signal win-rate attribution */}
        {attr && attr.sample_size > 0 && (
          <Card icon={Target} title="Signal win-rate (approved alerts)" testId="blueprint-attribution" accent="emerald">
            <div className="text-xs text-neutral-600 mb-3">
              Rolling per-signal win rate across the last {attr.sample_size} approved alerts.
              "Won" = WIN_* outcome OR ACTIVE with positive drift. Numbers with fewer than
              5 settled cases are shown italicised — not enough data yet.
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {Object.entries(attr.by_signal).map(([key, s]) => {
                const measurable = (s.settled ?? 0) >= 5;
                const rate = s.win_rate;
                const rateColor = rate == null ? "text-neutral-400"
                  : rate >= 65 ? "text-mb-emerald-dark"
                    : rate >= 50 ? "text-mb-ink"
                      : "text-red-500";
                return (
                  <div key={key} className="rounded-md border border-mb-border bg-neutral-50 p-3" data-testid={`attr-${key}`}>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">{key}</div>
                    <div className={`mt-1 font-mono-data text-xl font-bold ${rateColor} ${measurable ? "" : "italic"}`}>
                      {rate == null ? "—" : `${rate}%`}
                    </div>
                    <div className="text-[10px] text-neutral-500 font-mono-data mt-1">
                      {s.fired} fired · {s.won}W / {s.lost}L
                      {s.pending > 0 && ` · ${s.pending} pending`}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Stage 3 — Macro overlay */}
        <Card icon={Radar} title="Stage 3 · Macro Overlay" testId="blueprint-stage3">
          <ul className="space-y-1 text-sm text-neutral-700">
            {data.stage3_macro_overlay.checks.map((c) => (
              <li key={c} className="flex items-start gap-2">
                <span className="text-mb-emerald-dark">·</span>
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </Card>

        {/* Runtime knobs */}
        <Card icon={Settings2} title="Live runtime knobs" testId="blueprint-knobs">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Object.entries(data.runtime_knobs).filter(([k]) => k !== "editable_at").map(([k, v]) => (
              <Stat key={k} label={k.replaceAll("_", " ")} value={v === null || v === undefined ? "—" : String(v)} />
            ))}
          </div>
          <div className="mt-3 text-xs text-neutral-600">
            Editable at: <b className="text-mb-ink">{data.runtime_knobs.editable_at}</b>
          </div>
        </Card>

        {/* Exclusions */}
        <Card icon={EyeOff} title="Explicit exclusions" testId="blueprint-exclusions">
          <ul className="space-y-1 text-sm text-neutral-700">
            {data.explicit_exclusions.map((e) => (
              <li key={e} className="flex items-start gap-2">
                <span className="text-neutral-400">×</span>
                <span>{e}</span>
              </li>
            ))}
          </ul>
        </Card>

        {/* Gaps */}
        <Card
          icon={AlertTriangle}
          title="Known coverage gaps"
          accent="amber"
          testId="blueprint-gaps"
        >
          <ul className="space-y-2 text-sm">
            {data.known_coverage_gaps.map((g) => (
              <li key={g.gap} className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5">▲</span>
                <div>
                  <div className="font-semibold text-mb-ink">{g.gap}</div>
                  <div className="text-xs text-neutral-600 italic">{g.example}</div>
                </div>
              </li>
            ))}
          </ul>
        </Card>

        {/* Suggested tweaks */}
        <Card
          icon={Sparkles}
          title="Suggested tweaks"
          accent="emerald"
          testId="blueprint-tweaks"
        >
          <ol className="space-y-2 text-sm text-mb-ink list-decimal list-inside">
            {data.suggested_tweaks.map((t) => (
              <li key={t} className="leading-snug">{t}</li>
            ))}
          </ol>
        </Card>

        <div className="mt-6 text-[11px] text-neutral-500 font-mono-data text-right">
          Generated {new Date(data.generated_at).toLocaleString()}
        </div>
      </div>
    </div>
  );
}

function Card({ icon: Icon, title, children, testId, accent }) {
  const border =
    accent === "amber" ? "border-l-4 border-amber-400"
      : accent === "emerald" ? "border-l-4 border-mb-emerald"
        : "";
  return (
    <div
      className={`mt-6 rounded-md bg-white border border-mb-border shadow-sm p-5 ${border}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-mb-violet" />
        <h2 className="uppercase tracking-widest text-[11px] font-bold text-mb-ink">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, muted }) {
  return (
    <div className={`rounded-md px-3 py-2 ${muted ? "bg-neutral-50 border border-mb-border" : "bg-mb-emerald/10 border border-mb-emerald/20"}`}>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">{label}</div>
      <div className="text-lg font-bold text-mb-ink font-mono-data">{value}</div>
    </div>
  );
}
